import {
  clearChatState,
  getAnamAiToken,
  setBotMessage,
  setCertificateSessionId,
  setMessages,
  setProductSessionId,
  setScenarioSessionId,
  setServerStatus,
} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
  AnamEvent,
  ClientError,
  ConnectionClosedCode,
  MessageRole,
  createClient,
} from "@anam-ai/js-sdk";
import {waitForVideoElement} from "@hooks/AnamAvatar/waitForVideoElement";
import {
  ANAM_AVATAR_ID,
  ANAM_CARTESIA_VOICE_GENERATION_OPTIONS,
  ANAM_INLINE_NAME,
  ANAM_LLM_CUSTOMER_CLIENT_V1,
  ANAM_LLM_ID,
  ANAM_PERSONA_ID,
  ANAM_SYSTEM_PROMPT,
  ANAM_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
  ANAM_TOKEN_REQUEST_ERROR_CODE,
  ANAM_VOICE_ID,
  ANAM_VOICE_PROVIDER,
  AnamTokenError,
} from "@hooks/AnamAvatar/utils";
import {
  AvatarSessionState,
  useAvatarLifecycleMachine,
} from "@hooks/HeygenAvatar/useAvatarLifecycleMachine";
import {useAvatarTransport} from "@hooks/HeygenAvatar/useAvatarTransport";
import {LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE} from "@hooks/HeygenAvatar/utils";
import {AvatarErrorPopup} from "@popups/simulationPopups/AvatarErrorPopup";
import {unwrapResult} from "@reduxjs/toolkit";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector, useStore} from "react-redux";
import {v4 as uuidv4} from "uuid";

const ANAM_MAX_SESSION_LENGTH_CAP_SEC = 20 * 60;
const ANAM_INBOUND_STATS_INTERVAL_MS = 4000;
const ANAM_RECONNECT_MAX_ATTEMPTS = 2;
const ANAM_RECONNECT_BASE_DELAY_MS = 1200;
/** SDK создаёт RTCPeerConnection после WS/сигналинга, не в момент await streamToVideoElement */
const ANAM_PC_DEBUG_WAIT_MS = 15000;
const ANAM_PC_DEBUG_POLL_MS = 100;
const ANAM_FORCED_EMOTION_TAG = '<emotion value="angry" />';

const strTrim = (v) => (typeof v === "string" ? v.trim() : "");
const sanitizeTextForAngryCartesia = (text) =>
  String(text || "")
    .replace(/<emotion\s+value="[^"]*"\s*\/>/gi, "")
    .replace(/\[laughter\]/gi, "")
    .trim();
const toAngryCartesiaSpeech = (text) => {
  const clean = sanitizeTextForAngryCartesia(text);
  if (!clean) return "";
  return `${ANAM_FORCED_EMOTION_TAG}${clean.startsWith(" ") ? "" : " "}${clean}`;
};

/**
 * @anam-ai/js-sdk склеивает baseUrl + apiVersion (`/v1`), пути вида `/v1/engine/session`.
 * Если бэк отдаёт `https://api.anam.ai/v1/`, получается `/v1/v1/...` и 404.
 */
const normalizeAnamSdkApiBaseUrl = (raw) => {
  if (typeof raw !== "string") return null;
  let u = raw.trim().replace(/\/+$/, "");
  if (/\/v1$/i.test(u)) u = u.replace(/\/v1$/i, "").replace(/\/+$/, "");
  return u || null;
};

/** Доступ к PC как у SDK (не в публичных типах). */
const getAnamPeerConnection = (client) =>
  client?.streamingClient?.peerConnection ?? null;

const pickInboundVideoRtpReport = async (client) => {
  const pc = getAnamPeerConnection(client);
  if (!pc || pc.connectionState === "closed") {
    return null;
  }
  const stats = await pc.getStats();
  let videoReport = null;
  stats.forEach((report) => {
    if (report.type === "inbound-rtp" && report.kind === "video") {
      videoReport = report;
    }
  });
  return videoReport;
};

const anamConnectionClosedHint = (reason) => {
  switch (reason) {
    case ConnectionClosedCode.WEBRTC_FAILURE:
      return "ICE/WebRTC: UDP/TURN, firewall, нет inbound video — смотри [Anam] rtc.snapshot и chrome://webrtc-internals";
    case ConnectionClosedCode.SIGNALLING_CLIENT_CONNECTION_FAILURE:
      return "Сигналинг (HTTP/WS до LiveKit): прокси, TLS, baseUrl, токен";
    case ConnectionClosedCode.SERVER_CLOSED_CONNECTION:
      return "Сервер Anam закрыл сессию (лимит, политика, ошибка румы)";
    case ConnectionClosedCode.MICROPHONE_PERMISSION_DENIED:
      return "Микрофон отклонён в браузере";
    case ConnectionClosedCode.NORMAL:
      return "Нормальное закрытие";
    default:
      return "См. reason/details от SDK";
  }
};

const pickPreferredCandidatePairReport = (stats) => {
  let nominated = null;
  let succeeded = null;
  stats.forEach((r) => {
    if (r.type !== "candidate-pair") return;
    if (r.state === "succeeded") succeeded = r;
    if (r.nominated) nominated = r;
  });
  return succeeded || nominated;
};

const logAnamRtcSnapshot = async (pc, why) => {
  if (!pc || pc.connectionState === "closed") return;
  try {
    const stats = await pc.getStats();
    const pair = pickPreferredCandidatePairReport(stats);
    let localCand = null;
    let remoteCand = null;
    if (pair?.localCandidateId) {
      stats.forEach((r) => {
        if (r.id === pair.localCandidateId && r.type === "local-candidate") {
          localCand = r;
        }
        if (r.id === pair.remoteCandidateId && r.type === "remote-candidate") {
          remoteCand = r;
        }
      });
    }
    let inboundVideo = null;
    stats.forEach((r) => {
      if (r.type === "inbound-rtp" && r.kind === "video") {
        inboundVideo = r;
      }
    });
    console.warn("[Anam] rtc.snapshot", {
      why,
      iceConnectionState: pc.iceConnectionState,
      connectionState: pc.connectionState,
      candidatePairState: pair?.state,
      candidatePairNominated: pair?.nominated,
      localCandidateType: localCand?.candidateType,
      remoteCandidateType: remoteCand?.candidateType,
      inboundVideoBytesReceived: inboundVideo?.bytesReceived,
      inboundVideoFramesDecoded: inboundVideo?.framesDecoded,
    });
  } catch (err) {
    console.warn("[Anam] rtc.snapshot error", err);
  }
};

/**
 * Anam avatar session — зеркало возможностей useHeygenAvatar (токен с бэка, прокси, озвучка сообщений чата).
 * Видео: `mediaStream` ref + `videoElementId` на `<video>` (см. ChromaKeyVideoPlayer).
 */
export const useAnamAvatar = (
  openPopup = null,
  isSessionInitialized = false,
  hasAvatar = false,
  volume = 1,
) => {
  const dispatch = useDispatch();
  const store = useStore();
  const {t} = useTranslation();

  const {language} = useSelector((state) => state.common.settings);
  const {avatarId, voiceId, product_session_id, messages, proxyList} =
    useSelector((state) => state.ml.chat);

  const [videoElementId] = useState(() => `anam-avatar-${uuidv4()}`);
  const mediaStream = useRef(null);

  const {
    avatarSessionStateRef,
    avatarSessionState,
    setAvatarSessionState,
    activityState,
    setActivityState,
    messageState,
    setMessageState,
    silenceState,
    setSilenceState,
  } = useAvatarLifecycleMachine();

  const anamClientRef = useRef(null);
  const detachClientListenersRef = useRef(null);
  const anamInboundStatsIntervalRef = useRef(null);
  const anamInboundStatsPrevRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectInProgressRef = useRef(false);
  const initAnamWithTokenRef = useRef(null);
  const lastTalkAtRef = useRef(null);
  const microphonePermissionGrantedRef = useRef(false);
  const isStoppingRef = useRef(false);
  const speakQueueRef = useRef(Promise.resolve());
  const lastSpokenMessageIdRef = useRef(null);
  const anamPcDebugCleanupRef = useRef(null);

  const clearAnamPcDebugLogging = useCallback(() => {
    anamPcDebugCleanupRef.current?.();
    anamPcDebugCleanupRef.current = null;
  }, []);

  const attachAnamPeerConnectionDebugLogging = useCallback(
    (client) => {
      clearAnamPcDebugLogging();

      let waitTimer = null;
      let listenersCleanup = null;

      const detachListeners = () => {
        listenersCleanup?.();
        listenersCleanup = null;
      };

      const fullCleanup = () => {
        if (waitTimer != null) {
          window.clearInterval(waitTimer);
          waitTimer = null;
        }
        detachListeners();
      };

      const registerListeners = (pc) => {
        const onIce = () => {
          console.info("[Anam] pc.iceConnectionState", pc.iceConnectionState);
          if (
            pc.iceConnectionState === "failed" ||
            pc.iceConnectionState === "disconnected"
          ) {
            void logAnamRtcSnapshot(pc, `ice:${pc.iceConnectionState}`);
          }
        };

        const onConn = () => {
          console.info("[Anam] pc.connectionState", pc.connectionState);
          if (
            pc.connectionState === "failed" ||
            pc.connectionState === "disconnected"
          ) {
            void logAnamRtcSnapshot(pc, `conn:${pc.connectionState}`);
          }
        };

        pc.addEventListener("iceconnectionstatechange", onIce);
        pc.addEventListener("connectionstatechange", onConn);

        listenersCleanup = () => {
          pc.removeEventListener("iceconnectionstatechange", onIce);
          pc.removeEventListener("connectionstatechange", onConn);
        };
      };

      const tryAttach = () => {
        const pc = getAnamPeerConnection(client);
        if (!pc) {
          return false;
        }
        registerListeners(pc);
        if (waitTimer != null) {
          window.clearInterval(waitTimer);
          waitTimer = null;
          console.info(
            "[Anam] pc.debug: RTCPeerConnection появился, слушатели ice/connection повешены",
          );
        }
        return true;
      };

      if (tryAttach()) {
        anamPcDebugCleanupRef.current = fullCleanup;
        return;
      }

      const maxAttempts = Math.ceil(
        ANAM_PC_DEBUG_WAIT_MS / ANAM_PC_DEBUG_POLL_MS,
      );
      let attempts = 0;
      waitTimer = window.setInterval(() => {
        if (anamClientRef.current !== client) {
          fullCleanup();
          anamPcDebugCleanupRef.current = null;
          return;
        }
        attempts += 1;
        if (tryAttach()) {
          return;
        }
        if (attempts >= maxAttempts) {
          window.clearInterval(waitTimer);
          waitTimer = null;
          console.warn(
            "[Anam] pc.debug: за 15s не появился RTCPeerConnection на streamingClient (сигналинг не дошёл или клиент уже сменился)",
          );
        }
      }, ANAM_PC_DEBUG_POLL_MS);

      anamPcDebugCleanupRef.current = fullCleanup;
    },
    [clearAnamPcDebugLogging],
  );

  const {enableTransportBoundary, disableTransportBoundary} =
    useAvatarTransport({
      dispatch,
      proxyList,
    });

  const startMessages = useMemo(() => [t("page.chat.start_message_1")], [t]);

  const anamLanguageCode = useMemo(() => {
    const normalized = String(language || "en").toLowerCase();
    const [primaryTag] = normalized.split("-");
    const supported = new Set(["en", "ru", "de", "es", "fr"]);
    return supported.has(primaryTag) ? primaryTag : "en";
  }, [language]);

  const buildPersonaSessionPayload = useCallback(() => {
    const sessionSec = store.getState().ml.chat.sessionDurationSec;
    const maxSessionLengthSeconds =
      Number.isFinite(sessionSec) && sessionSec > 0
        ? Math.max(60, Math.min(ANAM_MAX_SESSION_LENGTH_CAP_SEC, sessionSec))
        : ANAM_MAX_SESSION_LENGTH_CAP_SEC;

    const personaId = strTrim(ANAM_PERSONA_ID);
    if (personaId) {
      return {personaConfig: {personaId}};
    }

    const cfgAvatar = strTrim(ANAM_AVATAR_ID);
    const cfgVoice = strTrim(ANAM_VOICE_ID);
    const cfgLlm = strTrim(ANAM_LLM_ID);
    const llmId = cfgLlm || ANAM_LLM_CUSTOMER_CLIENT_V1;
    const cfgName = strTrim(ANAM_INLINE_NAME);
    const cfgSystem = strTrim(ANAM_SYSTEM_PROMPT);

    const effectiveAvatarId = cfgAvatar || String(avatarId || "").trim();
    const effectiveVoiceId = cfgVoice || String(voiceId || "").trim();
    const voiceProvider = String(ANAM_VOICE_PROVIDER || "")
      .trim()
      .toLowerCase();
    const voiceGenerationOptions =
      voiceProvider === "cartesia"
        ? ANAM_CARTESIA_VOICE_GENERATION_OPTIONS
        : null;

    const personaConfig = {
      name: cfgName || "Session",
      ...(effectiveAvatarId ? {avatarId: effectiveAvatarId} : {}),
      ...(effectiveVoiceId ? {voiceId: effectiveVoiceId} : {}),
      llmId,
      ...(voiceGenerationOptions ? {voiceGenerationOptions} : {}),
      ...(cfgSystem
        ? {systemPrompt: cfgSystem}
        : {
            systemPrompt:
              "You are a helpful assistant for a training simulation.",
          }),
      languageCode: anamLanguageCode,
      maxSessionLengthSeconds,
    };

    return {personaConfig};
  }, [anamLanguageCode, avatarId, store, voiceId]);

  const lastBotPayload = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.sender === "bot" && m?.messageId && String(m?.text || "").trim()) {
        return {messageId: m.messageId, text: m.text};
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (mediaStream?.current) {
      mediaStream.current.volume = volume;
    }
  }, [volume]);

  const ensureMicrophonePermission = useCallback(async () => {
    if (microphonePermissionGrantedRef.current) return;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach((track) => track.stop());
      microphonePermissionGrantedRef.current = true;
    } catch (error) {
      const microphoneError = new Error("Microphone permission is required");
      microphoneError.code = LIVEAVATAR_MICROPHONE_PERMISSION_ERROR_CODE;
      microphoneError.cause = error;
      throw microphoneError;
    }
  }, []);

  const fetchSessionToken = useCallback(async () => {
    const resultAction = await dispatch(
      getAnamAiToken({
        session: buildPersonaSessionPayload(),
      }),
    );

    if (!getAnamAiToken.fulfilled.match(resultAction)) {
      throw new AnamTokenError(
        ANAM_TOKEN_REQUEST_ERROR_CODE,
        "Unable to retrieve Anam session token",
      );
    }

    const payload = unwrapResult(resultAction);

    if (payload == null) {
      throw new AnamTokenError(
        ANAM_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
        "Anam token endpoint returned empty response",
      );
    }

    if (payload?.error) {
      throw new AnamTokenError(
        ANAM_TOKEN_REQUEST_ERROR_CODE,
        "Anam token backend error",
        {
          backendError: payload.error,
        },
      );
    }

    const sessionToken =
      payload?.sessionToken ||
      payload?.session_token ||
      payload?.data?.sessionToken;

    const urlRaw = payload?.url || payload?.data?.url || null;
    const url = normalizeAnamSdkApiBaseUrl(urlRaw);

    if (!sessionToken) {
      throw new AnamTokenError(
        ANAM_TOKEN_EMPTY_RESPONSE_ERROR_CODE,
        "Session token missing in Anam response",
        {availableKeys: Object.keys(payload || {})},
      );
    }

    return {sessionToken, url};
  }, [buildPersonaSessionPayload, dispatch]);

  const clearAnamInboundStatsPolling = useCallback(() => {
    if (anamInboundStatsIntervalRef.current != null) {
      window.clearInterval(anamInboundStatsIntervalRef.current);
      anamInboundStatsIntervalRef.current = null;
    }
    anamInboundStatsPrevRef.current = null;
  }, []);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(
    (reason = "connection_closed") => {
      if (isStoppingRef.current) return;
      if (reconnectInProgressRef.current || reconnectTimerRef.current != null) {
        return;
      }
      if (avatarSessionStateRef.current === AvatarSessionState.CONNECTED) {
        return;
      }
      if (reconnectAttemptRef.current >= ANAM_RECONNECT_MAX_ATTEMPTS) {
        console.warn("[Anam] reconnect skipped: max attempts reached", {
          attempts: reconnectAttemptRef.current,
          reason,
        });
        return;
      }

      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delayMs = ANAM_RECONNECT_BASE_DELAY_MS * attempt;
      console.warn("[Anam] scheduling reconnect", {reason, attempt, delayMs});

      reconnectTimerRef.current = window.setTimeout(async () => {
        reconnectTimerRef.current = null;
        if (isStoppingRef.current) return;
        if (avatarSessionStateRef.current === AvatarSessionState.CONNECTED)
          return;

        const reconnectRunner = initAnamWithTokenRef.current;
        if (typeof reconnectRunner !== "function") {
          console.warn("[Anam] reconnect runner missing");
          return;
        }

        reconnectInProgressRef.current = true;
        try {
          await reconnectRunner();
          reconnectAttemptRef.current = 0;
          console.info("[Anam] reconnect succeeded", {attempt});
        } catch (error) {
          console.error("[Anam] reconnect failed", {attempt, error});
          scheduleReconnect("reconnect_failed");
        } finally {
          reconnectInProgressRef.current = false;
        }
      }, delayMs);
    },
    [avatarSessionStateRef],
  );

  const attachClientListeners = useCallback(
    (client) => {
      detachClientListenersRef.current?.();

      const onStreamEvent = (messageEvent) => {
        if (messageEvent?.role === MessageRole.PERSONA) {
          const talking = !messageEvent.endOfSpeech;
          setActivityState((prev) => ({
            ...prev,
            isAvatarTalking: talking,
          }));
          setSilenceState((prev) => ({
            ...prev,
            stopVoice: Boolean(messageEvent.endOfSpeech),
          }));
        }
      };

      const onServerWarning = (message) => {
        console.warn("[Anam] SERVER_WARNING", {message});
      };

      const onConnectionEstablished = () => {
        console.info("[Anam] CONNECTION_ESTABLISHED");
      };

      const onSessionReady = (sessionId) => {
        console.info("[Anam] SESSION_READY", {sessionId});
      };

      const onClosed = (reason, details) => {
        const shouldReconnect = !isStoppingRef.current;
        const now = Date.now();
        const msSinceLastTalk =
          typeof lastTalkAtRef.current === "number"
            ? now - lastTalkAtRef.current
            : null;
        void logAnamRtcSnapshot(
          getAnamPeerConnection(client),
          "sdk:CONNECTION_CLOSED",
        );
        console.warn("[Anam] CONNECTION_CLOSED", {
          reason,
          details,
          hint: anamConnectionClosedHint(reason),
          shouldReconnect,
          avatarSessionState: avatarSessionStateRef.current,
          msSinceLastTalk,
          lastTalkAt: lastTalkAtRef.current,
          closedAt: now,
        });
        setActivityState((prev) => ({...prev, isAvatarTalking: false}));
        setSilenceState((prev) => ({...prev, stopVoice: true}));
        avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
        setAvatarSessionState(AvatarSessionState.INACTIVE);
        if (shouldReconnect) {
          scheduleReconnect("connection_closed");
        }
      };

      client.addListener(
        AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED,
        onStreamEvent,
      );
      client.addListener(AnamEvent.CONNECTION_CLOSED, onClosed);
      client.addListener(AnamEvent.SERVER_WARNING, onServerWarning);
      client.addListener(
        AnamEvent.CONNECTION_ESTABLISHED,
        onConnectionEstablished,
      );
      client.addListener(AnamEvent.SESSION_READY, onSessionReady);

      detachClientListenersRef.current = () => {
        client.removeListener(
          AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED,
          onStreamEvent,
        );
        client.removeListener(AnamEvent.CONNECTION_CLOSED, onClosed);
        client.removeListener(AnamEvent.SERVER_WARNING, onServerWarning);
        client.removeListener(
          AnamEvent.CONNECTION_ESTABLISHED,
          onConnectionEstablished,
        );
        client.removeListener(AnamEvent.SESSION_READY, onSessionReady);
      };
    },
    [
      avatarSessionStateRef,
      scheduleReconnect,
      setActivityState,
      setAvatarSessionState,
      setSilenceState,
    ],
  );

  const startAnamInboundStatsPolling = useCallback(
    (client) => {
      clearAnamInboundStatsPolling();
      anamInboundStatsIntervalRef.current = window.setInterval(async () => {
        try {
          const report = await pickInboundVideoRtpReport(client);
          if (!report) {
            return;
          }
          const now = performance.now();
          const bytes = report.bytesReceived;

          anamInboundStatsPrevRef.current =
            typeof bytes === "number" ? {bytes, t: now} : null;
        } catch (err) {
          console.warn("[Anam] inbound-rtp stats error", err);
        }
      }, ANAM_INBOUND_STATS_INTERVAL_MS);
    },
    [clearAnamInboundStatsPolling],
  );

  const stopAnam = useCallback(async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    try {
      clearAnamPcDebugLogging();
      clearAnamInboundStatsPolling();
      detachClientListenersRef.current?.();
      detachClientListenersRef.current = null;

      if (anamClientRef.current) {
        try {
          await anamClientRef.current.stopStreaming();
        } catch (error) {
          console.error("Anam stopStreaming error:", error);
        }
        anamClientRef.current = null;
      }

      speakQueueRef.current = Promise.resolve();
      lastSpokenMessageIdRef.current = null;

      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      setActivityState((prev) => ({...prev, isAvatarTalking: false}));
      setMessageState({
        lastSpokenMessageId: null,
        firstMessageSent: false,
      });
      setSilenceState({
        silence: false,
        countdown: 115,
        stopVoice: true,
      });
      clearReconnectTimer();
      reconnectAttemptRef.current = 0;
      reconnectInProgressRef.current = false;
    } finally {
      isStoppingRef.current = false;
      disableTransportBoundary();
    }
  }, [
    avatarSessionStateRef,
    clearAnamInboundStatsPolling,
    clearAnamPcDebugLogging,
    clearReconnectTimer,
    disableTransportBoundary,
    setActivityState,
    setAvatarSessionState,
    setMessageState,
    setSilenceState,
  ]);

  const clearAllAnamState = useCallback(async () => {
    try {
      await stopAnam();
      dispatch(clearChatState());
      dispatch(setProductSessionId(null));
      dispatch(setScenarioSessionId(null));
      dispatch(setCertificateSessionId(null));
      dispatch(setServerStatus(""));
    } catch (error) {
      console.error("Error clearing Anam state:", error);
    }
  }, [dispatch, stopAnam]);

  const enqueueSpeakTask = useCallback((task) => {
    const run = speakQueueRef.current
      .then(() => task())
      .catch((error) => {
        console.error("Error Anam talk:", error);
      });
    speakQueueRef.current = run;
    return run;
  }, []);

  const speakBotMessageById = useCallback(
    (messageId, text) =>
      enqueueSpeakTask(async () => {
        const client = anamClientRef.current;
        if (!client) return;
        if (avatarSessionStateRef.current !== AvatarSessionState.CONNECTED) {
          return;
        }
        const speechText = toAngryCartesiaSpeech(text);
        if (!speechText || !messageId) return;
        if (lastSpokenMessageIdRef.current === messageId) return;

        lastSpokenMessageIdRef.current = messageId;
        setMessageState((prev) => ({
          ...prev,
          lastSpokenMessageId: messageId,
        }));

        setSilenceState((prev) => ({
          ...prev,
          countdown: 115,
          silence: false,
          stopVoice: false,
        }));

        lastTalkAtRef.current = Date.now();
        await client.talk(speechText);
      }),
    [enqueueSpeakTask, setMessageState, setSilenceState],
  );

  const speakText = useCallback(
    (text, isSilenceMessage = false) => {
      if (!isSilenceMessage) {
        return Promise.resolve();
      }
      return enqueueSpeakTask(async () => {
        const client = anamClientRef.current;
        if (!client) return;
        if (avatarSessionStateRef.current !== AvatarSessionState.CONNECTED) {
          return;
        }
        const speechText = toAngryCartesiaSpeech(text);
        if (!speechText) return;

        setSilenceState((prev) => ({
          ...prev,
          countdown: 115,
          silence: false,
          stopVoice: false,
        }));

        lastTalkAtRef.current = Date.now();
        await client.talk(speechText);
      });
    },
    [enqueueSpeakTask, setSilenceState],
  );

  const firstProductMessage = useCallback(async () => {
    if (
      product_session_id &&
      messages.length === 0 &&
      !messageState.firstMessageSent
    ) {
      const text =
        startMessages[Math.floor(Math.random() * startMessages.length)];
      const startMessage = {messageId: uuidv4(), text, sender: "bot"};

      await dispatch(setBotMessage(text));
      await dispatch(setMessages(startMessage));
      setMessageState((prev) => ({...prev, firstMessageSent: true}));
    }
  }, [
    dispatch,
    messageState.firstMessageSent,
    messages.length,
    product_session_id,
    setMessageState,
    startMessages,
  ]);

  const initAnamWithToken = useCallback(async () => {
    const personaSessionPayload = buildPersonaSessionPayload();
    const pc = personaSessionPayload.personaConfig || {};
    const okPersonaId = Boolean(pc.personaId);
    const okInline = Boolean(pc.avatarId && pc.voiceId && pc.llmId);
    if (!okPersonaId && !okInline) {
      throw new Error(
        "Anam: задайте ANAM_PERSONA_ID или ANAM_AVATAR_ID + ANAM_VOICE_ID в hooks/AnamAvatar/utils.js (llmId по умолчанию CUSTOMER_CLIENT_V1 для своего LLM + talk).",
      );
    }

    if (avatarSessionStateRef.current !== AvatarSessionState.INACTIVE) {
      throw new Error("Anam session already active");
    }

    await ensureMicrophonePermission();
    await enableTransportBoundary();

    avatarSessionStateRef.current = AvatarSessionState.CONNECTING;
    setAvatarSessionState(AvatarSessionState.CONNECTING);

    try {
      clearAnamPcDebugLogging();

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      const videoEl = await waitForVideoElement(videoElementId);
      mediaStream.current = videoEl;

      const {sessionToken, url} = await fetchSessionToken();

      const clientOptions =
        url && url.length > 0 ? {api: {baseUrl: url}} : undefined;

      const client = createClient(sessionToken, clientOptions);
      anamClientRef.current = client;
      attachClientListeners(client);

      await client.streamToVideoElement(videoElementId);
      attachAnamPeerConnectionDebugLogging(client);
      clearReconnectTimer();
      reconnectAttemptRef.current = 0;
      reconnectInProgressRef.current = false;
      avatarSessionStateRef.current = AvatarSessionState.CONNECTED;
      setAvatarSessionState(AvatarSessionState.CONNECTED);
      startAnamInboundStatsPolling(client);
    } catch (error) {
      clearAnamPcDebugLogging();
      clearAnamInboundStatsPolling();
      detachClientListenersRef.current?.();
      detachClientListenersRef.current = null;
      anamClientRef.current = null;
      avatarSessionStateRef.current = AvatarSessionState.INACTIVE;
      setAvatarSessionState(AvatarSessionState.INACTIVE);
      disableTransportBoundary();
      throw error;
    }
  }, [
    attachAnamPeerConnectionDebugLogging,
    attachClientListeners,
    avatarSessionStateRef,
    disableTransportBoundary,
    clearAnamInboundStatsPolling,
    clearAnamPcDebugLogging,
    clearReconnectTimer,
    enableTransportBoundary,
    ensureMicrophonePermission,
    buildPersonaSessionPayload,
    fetchSessionToken,
    startAnamInboundStatsPolling,
    setAvatarSessionState,
    videoElementId,
  ]);

  useEffect(() => {
    initAnamWithTokenRef.current = initAnamWithToken;
  }, [initAnamWithToken]);

  const showAnamError = useCallback(
    (onTryAgain, onExit, error = null) => {
      let detailedMessage = "";
      if (error instanceof ClientError) {
        detailedMessage = error.message;
      } else if (error?.code === ANAM_TOKEN_EMPTY_RESPONSE_ERROR_CODE) {
        detailedMessage = t("popup.avatar_error.token_empty_response");
      } else if (error?.code === ANAM_TOKEN_REQUEST_ERROR_CODE) {
        detailedMessage = t("popup.avatar_error.token_request_error");
      }

      if (!openPopup) return;

      openPopup(
        <AvatarErrorPopup
          onClickTryAgain={onTryAgain}
          onClickExit={onExit}
          detailedMessage={detailedMessage}
        />,
        {closeButton: false},
      );
    },
    [openPopup, t],
  );

  useEffect(() => {
    if (!lastBotPayload) return;
    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;
    void speakBotMessageById(lastBotPayload.messageId, lastBotPayload.text);
  }, [
    avatarSessionState,
    lastBotPayload?.messageId,
    lastBotPayload?.text,
    speakBotMessageById,
  ]);

  useEffect(() => {
    if (
      product_session_id &&
      !messageState.firstMessageSent &&
      isSessionInitialized &&
      (!hasAvatar || avatarSessionState === AvatarSessionState.CONNECTED)
    ) {
      firstProductMessage();
    }
  }, [
    avatarSessionState,
    firstProductMessage,
    hasAvatar,
    isSessionInitialized,
    messageState.firstMessageSent,
    product_session_id,
  ]);

  useEffect(() => {
    if (silenceState.countdown <= 0) {
      setSilenceState((prev) => ({...prev, silence: true}));
      return;
    }

    if (avatarSessionState !== AvatarSessionState.CONNECTED) return;

    const timer = setInterval(() => {
      setSilenceState((prev) => ({...prev, countdown: prev.countdown - 1}));
    }, 1000);

    return () => clearInterval(timer);
  }, [avatarSessionState, setSilenceState, silenceState.countdown]);

  useEffect(() => {
    if (
      avatarSessionState === AvatarSessionState.CONNECTED &&
      silenceState.silence
    ) {
      speakText(t("page.chat.silence_message"), true);
    } else if (silenceState.silence) {
      setSilenceState((prev) => ({
        ...prev,
        countdown: 115,
        silence: false,
      }));
    }
  }, [avatarSessionState, setSilenceState, silenceState.silence, speakText, t]);

  useEffect(() => {
    return () => {
      clearReconnectTimer();
      void clearAllAnamState();
    };
  }, [clearAllAnamState, clearReconnectTimer]);

  const sendUserMessage = useCallback((text) => {
    const client = anamClientRef.current;
    if (!client?.isStreaming?.()) return;
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    client.sendUserMessage(trimmed);
  }, []);

  const interruptPersona = useCallback(() => {
    anamClientRef.current?.interruptPersona();
  }, []);

  return {
    videoElementId,
    mediaStream,
    avatarSessionState,
    AvatarSessionState,
    isAvatarTalking: activityState.isAvatarTalking,
    silence: silenceState.silence,
    countdown: silenceState.countdown,
    stopVoice: silenceState.stopVoice,
    ensureMicrophonePermission,
    initAnamWithToken,
    stopAnam,
    sendUserMessage,
    interruptPersona,
    clearAllAnamState,
    showAnamError,
  };
};
